var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isAxiosError } from 'axios';
import { AuthenticationMethodType, AuthenticationReason, AuthenticationResult, AuthenticationStatus, AuthenticationSubject } from '@mc-alberta/types';
import { fidoAuthenticatorToMfaMethod, getAuthenticationContext } from './transforms';
import { getAuthenticator, getMfaMethodKpiMeta, createPublicKey, getPublicKey, isFidoDeviceBounded, isFidoDeviceRecognized, isSecurePaymentConfirmationSupported, isFidoSupported } from './utils';
import { Adapter } from '../Adapter';
import { AuthenticationStrategy } from '../../types/enums/AuthenticationStrategy';
import { DasAuthenticationStatus } from '../../types/enums/DasAuthenticationStatus';
import { DasService } from '../../types/enums/das-service';
import { KpiOperation } from '../../types/enums/KpiOperation';
import { KpiState } from '../../types/enums/KpiState';
import { transformDasError } from '../../loggers/utils';
import { ServiceErrorReason } from '../../types/enums/errors.enum';
import { axiosService } from '../../services/axios.service';
import { LifecycleAwaitable, LifecycleAwaiting } from '../../decorators/LifecycleAwaiter';
import { DEFAULT_FIDO_NAME, VERIFICATION_METHOD, KPI_RESOURCE } from '../../constants';
import { HttpHeader } from '../../types/enums/http-header.enum';
import { Kpi } from '../../loggers/Kpi';
export class FidoAdapter extends Adapter {
    constructor(params) {
        super(params);
        this.strategy = AuthenticationStrategy.FIDO2;
        this.verificationMethods = [VERIFICATION_METHOD.FIDO2];
        this.supportedReasons = [
            AuthenticationReason.TRANSACTION_AUTHENTICATION,
            AuthenticationReason.CONSUMER_IDENTITY_VALIDATION,
            AuthenticationReason.AUTHENTICATOR_REGISTRATION
        ];
        this.selectedMethodIndex = 0;
        /** Whether or not this device is eligible to use FIDO (enrollment or authentication). */
        this.isDeviceEligible = false;
        /**
         * Token cache for storing credentials to bind to.
         *
         * These credentials will be vacuously sent each time that `/authenticate` is called
         * for the purpose of binding that token's credential, if applicable.
         */
        this.bindingTokens = {};
        /**
         * Enrolls a new FIDO user.
         * On its first call, this method will request the necessary biometric from the user.
         */
        this.createCredential = (locale) => __awaiter(this, void 0, void 0, function* () {
            // credentials have been collected - send them to DAS and return its result
            const authenticator = yield this.post('registrations/completions', {
                publicKeyCredentials: this.publicKeyWithAttestation,
                compliance: {
                    consent: {
                        consents: [{ locale }, { locale }]
                    }
                }
            });
            this.deviceRecognitionToken = authenticator === null || authenticator === void 0 ? void 0 : authenticator.deviceRecognitionToken;
            return authenticator;
        });
        /**
         * Creates a new public key credential based on configuration retrieved from DAS.
         */
        this.fetchPublicKeyCredentialCreationOptions = (fidoUserName) => __awaiter(this, void 0, void 0, function* () {
            const consumerDeviceData = yield this.getConsumerDeviceData();
            const data = yield this.post('registrations/initiations', {
                authenticatorType: AuthenticationMethodType.FIDO2,
                consumerDeviceData,
                fidoUserName
            });
            this.publicKeyCredentialCreationOptions = data === null || data === void 0 ? void 0 : data.publicKeyCredentialCreationOptions;
        });
        /**
         * Returns the validated authenticator instance from a successful authentication response.
         */
        this.getValidatedAuthenticator = ({ methodAttributes: { unboundAuthenticator, boundAuthenticator }, proofOfAuthentication }) => {
            delete this.publicKeyWithAssertion;
            const authenticator = unboundAuthenticator
                ? { authenticator: unboundAuthenticator }
                : boundAuthenticator;
            if (authenticator) {
                authenticator.authenticator.assertionData = { proofOfAuthentication };
            }
            return authenticator;
        };
        /**
         * Returns device attributes, including the recognition token (if exists).
         */
        this.getConsumerDeviceData = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const deviceRecognitionToken = yield this.getDeviceRecognitionToken();
            this.isSpcSupported = yield isSecurePaymentConfirmationSupported();
            // Retrieve user agent data
            const userAgentData = yield ((_a = navigator.userAgentData) === null || _a === void 0 ? void 0 : _a.getHighEntropyValues(['platformVersion']));
            this.deviceRecognitionToken = deviceRecognitionToken;
            return {
                userAgent: navigator.userAgent,
                screenWidth: screen.width,
                screenHeight: screen.height,
                spcSupported: this.isSpcSupported,
                deviceRecognitionToken: deviceRecognitionToken || undefined,
                platformName: userAgentData === null || userAgentData === void 0 ? void 0 : userAgentData.platform,
                platformVersion: userAgentData === null || userAgentData === void 0 ? void 0 : userAgentData.platformVersion
            };
        });
        /**
         * Makes a `GET` request to DAS. This will attach any necessary identifiers/authorization.
         */
        this.get = (path, query) => __awaiter(this, void 0, void 0, function* () {
            const dasParameters = Object.assign({ client_id: this.dasClientId, client_session_id: this.srcCorrelationId, service: DasService.DIGITAL_AUTHENTICATION_SERVICE }, query);
            const dasUrlParameters = new URLSearchParams(dasParameters);
            const { data } = yield axiosService.get(`${this.dasHost}/${path}?${dasUrlParameters.toString()}`, {
                withCredentials: true,
                headers: {
                    'correlation-id': this.srcCorrelationId,
                    authorization: `Bearer ${this.accessToken}`
                }
            });
            return data;
        });
        /**
         * Makes a `POST` request to DAS. This will attach any necessary identifiers/authorization.
         */
        this.post = (path, params) => __awaiter(this, void 0, void 0, function* () {
            try {
                const { data } = yield axiosService.post(`${this.dasHost}/${path}`, Object.assign({ clientId: this.dasClientId, clientSessionId: this.srcCorrelationId, authenticationSessionId: this.dasAuthenticationSessionId }, params), {
                    withCredentials: true,
                    headers: {
                        'correlation-id': this.srcCorrelationId,
                        authorization: `Bearer ${this.accessToken}`
                    }
                });
                return data;
            }
            catch (error) {
                this.processAxiosError(error);
            }
        });
        this.setMethodAttributes = () => { };
        /**
         * Returns real-time KPI metadata.
         */
        this.kpiMeta = () => {
            var _a;
            return (Object.assign(Object.assign(Object.assign({}, this.consumerKpiMeta()), this.cardKpiMeta()), { authenticationData: {
                    multiFactorAuthenticatorData: Object.assign(Object.assign({}, getMfaMethodKpiMeta(this.authenticationMethod, {
                        spcSupported: this.isSpcSupported,
                        isDeviceRecognizedForFido: !!this.deviceRecognitionToken
                    }, this.authenticator)), { dasAuthenticationSessionId: this.dasAuthenticationSessionId, dasClientId: this.dasClientId })
                }, resource: ((_a = this.authenticationMethod) === null || _a === void 0 ? void 0 : _a.authenticationSubject) === AuthenticationSubject.CONSUMER
                    ? KPI_RESOURCE.CONSUMER
                    : KPI_RESOURCE.CARD }));
        };
        this.kpi = new Kpi(params, this.kpiMeta);
    }
    /** Whether or not the cardholder and/or device can be enrolled into FIDO. */
    get isEnrollable() {
        if (!this.authenticationMethod)
            return false;
        if (isFidoDeviceRecognized(this.authenticationMethod)) {
            // device is recognized - cardholder can enroll as long as it's not already bounded to the card
            return !isFidoDeviceBounded(this.authenticationMethod);
        }
        return this.isDeviceEligible; // device is not recognized
    }
    /** True if FIDO can be used for authentication. */
    get isAuthenticatable() {
        var _a, _b, _c;
        if (!((_c = (_b = (_a = this.authenticationMethod) === null || _a === void 0 ? void 0 : _a.methodAttributes) === null || _b === void 0 ? void 0 : _b.extAuthProviderMetaData) === null || _c === void 0 ? void 0 : _c.accessToken)) {
            return false;
        }
        return super.isAuthenticatable && isFidoDeviceBounded(this.authenticationMethod);
    }
    applyAuthenticationMethods(authenticationMethods) {
        var _a, _b, _c;
        if (isFidoDeviceRecognized(this.authenticationMethod) || this.authenticator) {
            // if the device is already recognized then do not replace it
            return;
        }
        this.authenticationMethods = [];
        super.applyAuthenticationMethods(authenticationMethods);
        if (!((_c = (_b = (_a = this.authenticationMethod) === null || _a === void 0 ? void 0 : _a.methodAttributes) === null || _b === void 0 ? void 0 : _b.extAuthProviderMetaData) === null || _c === void 0 ? void 0 : _c.accessToken)) {
            // the DAS session access data is missing, and therefore no FIDO functions can occur
            this.authenticationMethods = [];
            this.authenticationStatus = AuthenticationStatus.NOT_SUPPORTED;
            return;
        }
        const { extAuthProviderMetaData } = this.authenticationMethod.methodAttributes;
        this.dasAuthenticationSessionId = extAuthProviderMetaData.authSessionId;
        this.accessToken = extAuthProviderMetaData.accessToken;
    }
    getLookupContext() {
        return __awaiter(this, void 0, void 0, function* () {
            this.isDeviceEligible = yield isFidoSupported();
            if (!this.isDeviceEligible)
                return {};
            return {
                consumerDeviceData: yield this.getConsumerDeviceData(),
                mfaMethod: this.authenticator
                    ? fidoAuthenticatorToMfaMethod(this.authenticator).mfaMethod
                    : undefined
            };
        });
    }
    /**
     * Returns the current `deviceRecognitionToken`.
     *
     * If its current value is `null`, that indicates that a recognition attempt was made but
     * the device is unrecognized.
     *
     * If its value is `undefined`, that indicates that no recognition attempt was made yet and,
     * should conditions be satisfied, will make an API call to attempt to recognize the device.
     */
    getDeviceRecognitionToken() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.deviceRecognitionToken !== undefined) {
                // if the token is defined (string or null) then we've attempted a lookup; return it
                return this.deviceRecognitionToken;
            }
            if (!this.authenticationReasons.length) {
                // don't attempt to fetch the token if there's no reason to use FIDO
                return;
            }
            if (!(yield isFidoSupported())) {
                // don't attempt to fetch the token if FIDO is impossible
                return undefined;
            }
            this.deviceRecognitionToken = yield this.fetchDeviceRecognitionToken();
            return this.deviceRecognitionToken;
        });
    }
    /**
     * Performs a device recognition API call to DAS.
     *
     * If the device is recognized for FIDO, a `deviceRecognitionToken` (JWT) is returned.
     * If the device is unrecognized, this will return `null`.
     */
    fetchDeviceRecognitionToken() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { data } = yield axiosService.get(`${this.dasHost}/devices/recognitions`, {
                    withCredentials: true,
                    params: {
                        client_id: this.dasClientId,
                        client_session_id: this.srcCorrelationId
                    },
                    headers: {
                        'correlation-id': this.srcCorrelationId
                    }
                });
                return data.deviceRecognitionToken || null;
            }
            catch (error) {
                if (!isAxiosError(error)) {
                    throw error;
                }
            }
        });
    }
    /**
     * Pre-loads an authentication session for FIDO2 authentication.
     */
    preloadSession() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isPreloaded) {
                return this.getSession();
            }
            this.isPreloaded = true;
            if (this.isAuthenticatable) {
                this.kpi.start(KpiOperation.INITIATE_AUTHENTICATION);
                yield this.authenticateCredential();
                this.authenticationStatus = AuthenticationStatus.PENDING_CHALLENGE;
                return this.getSession();
            }
            return this.getNotSupportedResult();
        });
    }
    initializeSession(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (params.isIdentityVerificationRequired) {
                return this.getNotSupportedResult();
            }
            return this.preloadSession();
        });
    }
    /**
     * Marshals WebAuthn to assert the public key credential.
     */
    provideChallenge() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.authenticationStatus = AuthenticationStatus.PENDING;
                this.kpi.start(KpiOperation.PROVIDE_AUTHENTICATION_CHALLENGE);
                this.publicKeyWithAssertion = yield getPublicKey(this.publicKeyCredentialRequestOptions);
                return this.getSession();
            }
            catch (error) {
                return this.destroySession(error);
            }
        });
    }
    /**
     * Validates the public key credentials.
     */
    validateSession() {
        return __awaiter(this, void 0, void 0, function* () {
            this.kpi.start(KpiOperation.COMPLETE_AUTHENTICATION);
            return this.validateAuthenticator({
                authenticator: yield this.authenticateCredential(),
                authenticationReason: this.authenticationReasons[0]
            });
        });
    }
    /**
     * Validates the public key credentials.
     */
    validateAuthenticator({ authenticationReason, authenticator }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (authenticator) {
                // validate the authenticator with authentication provider
                return this.refreshSession(
                // methodAttributes
                {
                    boundMfaMethod: fidoAuthenticatorToMfaMethod(authenticator),
                    extAuthProviderMetaData: {
                        authSessionId: this.dasAuthenticationSessionId,
                        accessToken: this.accessToken
                    }
                }, 
                // payload overrides
                {
                    authenticationContext: Object.assign(Object.assign({}, this.authenticationContext), { consumerDeviceData: yield this.getConsumerDeviceData(), authenticationReasons: [authenticationReason] })
                }, Object.assign(Object.assign({}, (this.bindingTokens.cardToken && {
                    [HttpHeader.X_SRC_CARD_TOKEN]: this.bindingTokens.cardToken
                })), (this.bindingTokens.idToken && {
                    [HttpHeader.X_SRC_MCID_TOKEN]: this.bindingTokens.idToken
                })));
            }
            // MACS authentication was not successful; return the most recent state
            return this.getSession();
        });
    }
    /**
     * Enrolls a credential to FIDO.
     * This will assert an existing passkey (if any) instead of creating a new one.
     */
    enroll(locale, fidoUserName = DEFAULT_FIDO_NAME) {
        return __awaiter(this, void 0, void 0, function* () {
            this.kpi.start(KpiOperation.INITIATE_FIDO_REGISTRATION);
            try {
                if (isFidoDeviceRecognized(this.authenticationMethod)) {
                    // start a new DAS authentication session for an existing credential
                    yield this.authenticateCredential();
                }
                else {
                    // fetch the publicKeyCredentialCreationOptions to create a new credential
                    yield this.fetchPublicKeyCredentialCreationOptions(fidoUserName);
                }
                if (this.publicKeyCredentialRequestOptions) {
                    this.kpi.end(KpiState.COMPLETED);
                    // authentication of existing, unbound credential
                    this.kpi.start(KpiOperation.PROVIDE_AUTHENTICATION_CHALLENGE);
                    this.publicKeyWithAssertion = yield getPublicKey(this.publicKeyCredentialRequestOptions);
                    this.authenticator = yield this.authenticateCredential();
                }
                else if (this.publicKeyCredentialCreationOptions) {
                    this.kpi.end(KpiState.COMPLETED);
                    // creation of a new credential
                    this.kpi.start(KpiOperation.PROVIDE_AUTHENTICATION_CHALLENGE);
                    this.publicKeyWithAttestation = yield createPublicKey(this.publicKeyCredentialCreationOptions);
                    this.authenticator = yield this.createCredential(locale);
                }
                else {
                    this.kpi.end(KpiState.FAILED);
                }
                this.kpi.start(KpiOperation.COMPLETE_FIDO_REGISTRATION);
                if (this.authenticator) {
                    this.authenticationStatus = AuthenticationStatus.PENDING;
                }
                else {
                    this.kpi.end(KpiState.FAILED);
                }
                return !!this.authenticator;
            }
            catch (error) {
                this.kpi.queueError(error);
                return false;
            }
        });
    }
    /**
     * Authenticates a FIDO user.
     * On its first call, this method will request the public key credential request options.
     * On its second call, this method will validate the credential.
     */
    authenticateCredential() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const consumerDeviceData = yield this.getConsumerDeviceData();
            const results = yield this.post('authentications', {
                authenticationContext: getAuthenticationContext(this.authenticationContext),
                authenticationMethod: Object.assign(Object.assign({}, (_a = getAuthenticator(this.authenticationMethod)) === null || _a === void 0 ? void 0 : _a.authenticator), { methodAttributes: {
                        publicKeyCredential: this.publicKeyWithAssertion
                    } }),
                consumerDeviceData
            });
            switch (results === null || results === void 0 ? void 0 : results.authenticationStatus) {
                case DasAuthenticationStatus.INITIATED:
                    this.publicKeyCredentialRequestOptions =
                        results.methodAttributes.publicKeyCredentialRequestOptions;
                    return;
                case DasAuthenticationStatus.COMPLETE:
                    return this.getValidatedAuthenticator(results);
                default:
                    throw new Error(results === null || results === void 0 ? void 0 : results.authenticationStatus);
            }
        });
    }
    /**
     * Binds the active authenticator.
     *
     * @returns true if binding was successful. False otherwise.
     */
    bindCredential() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.authenticator) {
                return false;
            }
            const { authenticationResult } = yield this.validateAuthenticator({
                authenticator: this.authenticator,
                authenticationReason: this.authenticationReasons[0] || AuthenticationReason.AUTHENTICATOR_REGISTRATION
            });
            delete this.authenticator;
            if (authenticationResult === AuthenticationResult.AUTHENTICATED) {
                this.kpi.end(KpiState.COMPLETED, this.onKpiSuccess());
            }
            else {
                this.kpi.end(KpiState.FAILED);
            }
            return authenticationResult === AuthenticationResult.AUTHENTICATED;
        });
    }
    bindSession() {
        return __awaiter(this, void 0, void 0, function* () {
            // token binding is not applicable for FIDO2
            return false;
        });
    }
    /**
     * This will call the DAS Service Configurations and abstract the supported locales object into
     * consumable data (primarily Tux Locale Picker)
     */
    getSupportedFidoLocales() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const dasConfigurationUrl = 'services/configurations';
                const dasConfigurations = yield this.get(dasConfigurationUrl);
                const { supportedCountries: supportedCountryConfigs } = dasConfigurations.serviceFunctions[0];
                return supportedCountryConfigs.reduce((supportedFidoLocales, country) => {
                    const { countryCode, supportedLanguages } = country;
                    const languageCodes = supportedLanguages.map(({ languageCode }) => {
                        return languageCode;
                    });
                    const defaultLanguageConfig = supportedLanguages.find(({ isDefault }) => isDefault);
                    // If no default language is found, default to undefined for legal reasons
                    const defaultLanguage = defaultLanguageConfig === null || defaultLanguageConfig === void 0 ? void 0 : defaultLanguageConfig.languageCode;
                    const supportedLocale = { countryCode, defaultLanguage, languageCodes };
                    supportedFidoLocales.push(supportedLocale);
                    return supportedFidoLocales;
                }, []);
            }
            catch (error) {
                this.destroySession(error);
                return [];
            }
        });
    }
    processAxiosError(error) {
        var _a, _b;
        const axiosError = error;
        const httpResponseCode = (_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.status;
        const errorData = (_b = axiosError.response) === null || _b === void 0 ? void 0 : _b.data;
        const dasError = errorData && transformDasError(errorData, httpResponseCode);
        const genericError = {
            reason: ServiceErrorReason.XHR_REQUEST_FAILED,
            httpResponseCode,
            message: `Request failed with status ${httpResponseCode}`
        };
        this.kpi.queueError(dasError || genericError);
    }
    toJSON() {
        return Object.assign(Object.assign({}, super.toJSON()), { authenticationMethods: [], accessToken: this.accessToken, authenticator: this.authenticator, isDeviceEligible: this.isDeviceEligible, publicKeyCredentialCreationOptions: this.publicKeyCredentialCreationOptions, publicKeyCredentialRequestOptions: this.publicKeyCredentialRequestOptions, publicKeyWithAssertion: this.publicKeyWithAssertion, publicKeyWithAttestation: this.publicKeyWithAttestation });
    }
}
__decorate([
    LifecycleAwaitable,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], FidoAdapter.prototype, "preloadSession", null);
__decorate([
    LifecycleAwaiting('preloadSession'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], FidoAdapter.prototype, "initializeSession", null);
